/**
 * Modal para cambiar el número de bultos vía PUT /orders/:id/change-packages
 */
import { useToast } from '@shared/hooks/use-toast';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { faBoxesStacked } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePackingService } from '../../hooks';
import type { Order } from '../../models';

export interface ChangePackagesModalProps {
  order: Order | null;
  onSuccess?: () => void;
}

export const ChangePackagesModal = forwardRef<ModalCenterRef, ChangePackagesModalProps>(({ order, onSuccess }, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const { t } = useTranslation('packingModule');
  const { toast } = useToast();
  const { adapter } = usePackingService();
  const MIN_BULTOS = 1;
  const MAX_BULTOS = 10;

  const [numBultos, setNumBultos] = useState(MIN_BULTOS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clampBultos = (value: number) => Math.min(MAX_BULTOS, Math.max(MIN_BULTOS, value));

  const handleNumBultosChange = (value: number) => {
    setNumBultos(clampBultos(value));
  };

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setNumBultos(MIN_BULTOS);
      modalRef.current?.openModal();
    },
    closeModal: () => modalRef.current?.closeModal(),
  }));

  const handleConfirm = async () => {
    if (!order?.id) return;

    const safeNumBultos = clampBultos(numBultos);
    if (safeNumBultos < MIN_BULTOS) {
      setNumBultos(MIN_BULTOS);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await adapter.changeOrderPackages(order.id, safeNumBultos);

      toast({
        title: t('toasts.change_packages_success.title'),
        description: response.message || t('toasts.change_packages_success.description'),
        variant: 'default',
      });
      onSuccess?.();
      modalRef.current?.closeModal();
    } catch (err) {
      const description = err instanceof Error ? err.message : t('toasts.change_packages_error.unexpected');
      toast({
        title: t('toasts.change_packages_error.title'),
        description,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!order) {
    return null;
  }

  return (
    <ModalCenter
      ref={modalRef}
      type="default"
      width="default"
      className="sm:max-w-[380px]"
      data-testid="change-packages-modal"
      title={
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBoxesStacked} className="w-3.5 h-3.5 text-icons" />
          {t('modals.adjust_packages.title')}
        </span>
      }
      onCloseModal={() => setNumBultos(MIN_BULTOS)}
    >
      {/* Descripción y límites API (1–10) - Description and API limits */}
      <div className="space-y-6">
        <p className="text-sm text-complementary-text">
          {t('modals.adjust_packages.description')}
          <span className="block mt-1 text-subtext">{t('modals.adjust_packages.range_hint')}</span>
        </p>

        <div className="flex justify-center">
          <StepperInput
            value={numBultos}
            onChange={handleNumBultosChange}
            min={MIN_BULTOS}
            max={MAX_BULTOS}
            step={1}
            allowNegative={false}
            className="w-full max-w-[220px]"
            data-testid="change-packages-stepper"
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            size="lg"
            className="border-card-border"
            data-testid="change-packages-cancel-button"
            disabled={isSubmitting}
            onClick={() => modalRef.current?.closeModal()}
          >
            {t('modals.adjust_packages.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="bg-primary text-btn-text"
            data-testid="change-packages-confirm-button"
            disabled={isSubmitting || numBultos < MIN_BULTOS}
            onClick={handleConfirm}
          >
            {isSubmitting ? t('modals.adjust_packages.saving') : t('modals.adjust_packages.confirm')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ChangePackagesModal.displayName = 'ChangePackagesModal';
